import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, IconButton, Stack, Paper, Toolbar } from '@mui/material';
import { 
  Print as PrintIcon, 
  Refresh as RefreshIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RotateLeft as RotateLeftIcon,
  RotateRight as RotateRightIcon,
  Fullscreen as FullscreenIcon,
  Download as DownloadIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { generateEmployeeFormPrintHTML } from '../../utils/printUtils';
import { employeesData } from '../../Data/Kontak/Employee';

const PrintEmployee = () => {
  const { id } = useParams();
  const [employee, setEmployee] = useState(null);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const iframeRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        // support static id used elsewhere
        if (String(id).startsWith('static_')) {
          const staticId = parseInt(String(id).replace('static_', ''));
          const staticEmployee = employeesData.find(emp => emp.id === staticId);
          if (staticEmployee) {
            setEmployee({ full_name: staticEmployee.personalData.employee_name, staticData: staticEmployee, isStatic: true });
            return;
          }
        }
        const resp = await fetch(`https://apidashboard.eurekagroup.id/employee/get/${id}`);
        if (resp.ok) {
          const data = await resp.json();
          setEmployee(data);
          return;
        }
        setError('Data karyawan tidak ditemukan.');
      } catch (e) {
        setError('Gagal memuat data karyawan.');
      }
    };
    load();
  }, [id]);

  const html = useMemo(() => (employee ? generateEmployeeFormPrintHTML(employee) : ''), [employee]);

  // Calculate pages after iframe loads
  useEffect(() => {
    if (!html) return;
    const iframe = iframeRef.current;
    if (!iframe) return;
    let scrollHandler = null;
    
    const onLoad = () => {
      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
        const iframeBody = iframeDoc.body;
        const iframeHtml = iframeDoc.documentElement;
        const iframeWindow = iframe.contentWindow;
        
        // Wait a bit for content to render
        setTimeout(() => {
          // Get actual content height
          const contentHeight = Math.max(
            iframeBody.scrollHeight,
            iframeBody.offsetHeight,
            iframeHtml.clientHeight,
            iframeHtml.scrollHeight,
            iframeHtml.offsetHeight
          );
          
          // A4 page height in pixels (297mm at 96 DPI ≈ 1123px)
          const pageHeightPx = 1123;
          const pages = Math.max(1, Math.ceil(contentHeight / pageHeightPx));
          
          setTotalPages(pages);
          
          // Add scroll listener to update current page
          scrollHandler = () => {
            const scrollTop = iframeWindow.scrollY || iframeWindow.pageYOffset || iframeBody.scrollTop;
            const currentPageNum = Math.min(
              pages,
              Math.max(1, Math.floor(scrollTop / pageHeightPx) + 1)
            );
            setCurrentPage(currentPageNum);
          };
          
          iframeWindow.addEventListener('scroll', scrollHandler);
        }, 500);
      } catch (_) {}
    };
    
    iframe.addEventListener('load', onLoad);
    
    return () => {
      iframe.removeEventListener('load', onLoad);
      if (scrollHandler && iframe.contentWindow) {
        iframe.contentWindow.removeEventListener('scroll', scrollHandler);
      }
    };
  }, [html]);

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 25, 200));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 25, 50));
  const handleRotateLeft = () => setRotation(prev => prev - 90);
  const handleRotateRight = () => setRotation(prev => prev + 90);
  const handlePrint = () => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.print();
    }
  };
  
  const handlePageClick = (pageNum) => {
    setCurrentPage(pageNum);
    const iframe = iframeRef.current;
    if (iframe?.contentWindow) {
      try {
        // A4 page height in pixels (297mm at 96 DPI ≈ 1123px)
        const pageHeightPx = 1123;
        const scrollPosition = (pageNum - 1) * pageHeightPx;
        iframe.contentWindow.scrollTo({
          top: scrollPosition,
          behavior: 'smooth'
        });
      } catch (_) {}
    }
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!employee) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 2 }}>
        <CircularProgress />
        <Typography>Menyiapkan print preview…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 9999,
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column', 
      bgcolor: '#525659',
      fontFamily: 'Roboto, Arial, sans-serif'
    }}>
      {/* Chrome-style Title Bar */}
      <Box sx={{ 
        height: 28, 
        bgcolor: '#2c2c2c', 
        borderBottom: '1px solid #424242',
        display: 'flex',
        alignItems: 'center',
        px: 2,
        fontSize: '12px',
        color: 'white'
      }}>
        <Typography variant="caption" sx={{ color: 'white', fontWeight: 500 }}>
          Formulir Data Karyawan - {employee?.full_name || 'Employee'}
        </Typography>
      </Box>

      {/* PDF Viewer Toolbar */}
      <Paper elevation={1} sx={{ borderRadius: 0, borderBottom: '1px solid #424242', bgcolor: '#2c2c2c' }}>
        <Toolbar variant="dense" sx={{ minHeight: 48, bgcolor: '#2c2c2c', px: 2 }}>
          <Typography variant="body2" sx={{ mr: 2, fontWeight: 500, color: 'white' }}>
            {currentPage} / {totalPages}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mx: 2 }}>
            <IconButton size="small" onClick={handleZoomOut} sx={{ color: 'white' }}>
              <ZoomOutIcon fontSize="small" />
            </IconButton>
            <Typography variant="body2" sx={{ minWidth: 40, textAlign: 'center', color: 'white' }}>
              {zoom}%
            </Typography>
            <IconButton size="small" onClick={handleZoomIn} sx={{ color: 'white' }}>
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
            <IconButton size="small" onClick={handleRotateLeft} sx={{ color: 'white' }}>
              <RotateLeftIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={handleRotateRight} sx={{ color: 'white' }}>
              <RotateRightIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={handlePrint} sx={{ color: 'white' }}>
              <PrintIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" sx={{ color: 'white' }}>
              <DownloadIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" sx={{ color: 'white' }}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Box>
        </Toolbar>
      </Paper>

      {/* Main Content Area */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left Sidebar - Thumbnails */}
        <Box sx={{ 
          width: 200, 
          bgcolor: '#424242', 
          borderRight: '1px solid #e0e0e0',
          overflowY: 'auto',
          p: 1
        }}>
          <Stack spacing={1}>
            {Array.from({ length: totalPages }, (_, index) => (
              <Paper
                key={index}
                elevation={currentPage === index + 1 ? 3 : 1}
                sx={{
                  p: 1,
                  cursor: 'pointer',
                  border: currentPage === index + 1 ? '2px solid #1976d2' : '1px solid #e0e0e0',
                  bgcolor: currentPage === index + 1 ? '#e3f2fd' : 'white',
                  '&:hover': { bgcolor: '#f5f5f5' }
                }}
                onClick={() => handlePageClick(index + 1)}
              >
                <Box sx={{ 
                  height: 120, 
                  bgcolor: '#f5f5f5', 
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  color: '#666'
                }}>
                  Page {index + 1}
                </Box>
                <Typography variant="caption" sx={{ textAlign: 'center', display: 'block', mt: 0.5 }}>
                  {index + 1}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </Box>

        {/* Document Viewer */}
        <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
          <Box
            ref={containerRef}
            sx={{
              width: '100%',
              height: '100%',
              overflow: 'auto',
              bgcolor: '#525659',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              p: 2
            }}
          >
            <Box
              sx={{
                transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
                transformOrigin: 'top center',
                transition: 'transform 0.2s ease-in-out',
                bgcolor: (theme) => theme.palette.background.paper,
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                borderRadius: 1,
                overflow: 'visible'
              }}
            >
              <iframe
                ref={iframeRef}
                title="print-employee"
                style={{
                  width: '210mm',
                  minHeight: '297mm',
                  border: 'none',
                  background: '#fff',
                  display: 'block'
                }}
                srcDoc={html}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PrintEmployee;